import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faSpinner, faTimes} from '@fortawesome/pro-solid-svg-icons';

import {
  ErrorBanner,
  JourneySelectInput,
  JourneyTextArea,
  JourneyTextInput,
} from '../../../shared/components';

interface ITeamRoleOption {
  id: number;
  name: string;
}

interface ITeamMemberRecord {
  id: number;
  name: string;
  description?: string | null;
  farmTeamRole: {
    id: number;
    name: string;
  } | null;
}

interface ITeamModalState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  memberId: number | null;
  name: string;
  roleId: number | '';
  description: string;
  error: string | null;
}

export interface ITeamSubStepProps {
  isLoadingTeam: boolean;
  preparedTeamMembers: ITeamMemberRecord[];
  teamDeleteLoading: Record<number, boolean>;
  pendingTeamDeleteId: number | null;
  teamModalState: ITeamModalState;
  teamRoles: ITeamRoleOption[];
  teamError: string | null;
  hasApplicationId: boolean;
  teamNameRequiredError: string;
  teamRoleRequiredError: string;
  teamRoleNotAvailableError: string;
  onOpenTeamModal: (
    mode: 'create' | 'edit',
    member?: ITeamMemberRecord,
  ) => void;
  onCloseTeamModal: () => void;
  onTeamModalSubmit: () => void;
  onRequestDeleteTeamMember: (member: ITeamMemberRecord) => void;
  onTeamModalNameChange: (value: string) => void;
  onTeamModalRoleChange: (value: string) => void;
  onTeamModalDescriptionChange: (value: string) => void;
}

export const TeamSubStep: React.FC<ITeamSubStepProps> = ({
  isLoadingTeam,
  preparedTeamMembers,
  teamDeleteLoading,
  pendingTeamDeleteId,
  teamModalState,
  teamRoles,
  teamError,
  hasApplicationId,
  teamNameRequiredError,
  teamRoleRequiredError,
  teamRoleNotAvailableError,
  onOpenTeamModal,
  onCloseTeamModal,
  onTeamModalSubmit,
  onRequestDeleteTeamMember,
  onTeamModalNameChange,
  onTeamModalRoleChange,
  onTeamModalDescriptionChange,
}) => (
  <div className="team-step">
    <header className="team-step__intro">
      <h3>Tell us about your team</h3>
      <p>
        Share your team&apos;s names and roles to complete your farm&apos;s
        profile.
      </p>
    </header>

    <div className="team-step__actions">
      <button
        type="button"
        className="money-btn money-btn--secondary money-btn--sm"
        onClick={() => onOpenTeamModal('create')}
        disabled={isLoadingTeam}
      >
        <FontAwesomeIcon icon={faPlus} /> Add team member
      </button>
    </div>

    <section
      className="team-step__list"
      aria-live="polite"
      aria-busy={isLoadingTeam}
    >
      {isLoadingTeam ? (
        <p className="team-step__status">
          <FontAwesomeIcon icon={faSpinner} spin role="status" /> Loading team
          members...
        </p>
      ) : null}

      {preparedTeamMembers.length === 0 && !isLoadingTeam ? (
        <p className="team-step__empty">
          Add the people who help you run your farm so we can understand your
          support structure.
        </p>
      ) : null}

      <ul className="team-step__items">
        {preparedTeamMembers.map((member) => {
          const memberId = Number(member.id);
          const isDeleting = Boolean(teamDeleteLoading[memberId]);

          return (
            <li key={member.id}>
              <article className="team-step__item">
                <div className="team-step__item-details">
                  <h4>{member.name}</h4>
                  <p className="team-step__role">
                    {member.farmTeamRole?.name || 'Role not specified'}
                  </p>
                  {member.description ? (
                    <p className="team-step__description">
                      {member.description}
                    </p>
                  ) : null}
                </div>

                <div className="team-step__item-actions">
                  <button
                    type="button"
                    className="money-btn money-btn--secondary money-btn--sm"
                    onClick={() => onOpenTeamModal('edit', member)}
                    disabled={isDeleting}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="money-btn money-btn--danger money-btn--sm"
                    onClick={() => onRequestDeleteTeamMember(member)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <FontAwesomeIcon icon={faSpinner} spin role="status" />
                    ) : pendingTeamDeleteId === memberId ? (
                      'Confirm delete'
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </section>

    {teamModalState.isOpen ? (
      <div
        className="team-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-modal-title"
      >
        <div
          className="team-modal__backdrop"
          aria-hidden="true"
          onClick={onCloseTeamModal}
        />
        <div className="team-modal__panel">
          <header className="team-modal__header">
            <div>
              <h3 id="team-modal-title">
                {teamModalState.mode === 'edit'
                  ? 'Edit team member'
                  : 'Add team member'}
              </h3>
              <p>
                Capture the basics so we know who to contact for each area of
                your operation.
              </p>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onCloseTeamModal}
              aria-label="Close team modal"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </header>

          <form
            className="team-modal__form"
            onSubmit={(event) => {
              event.preventDefault();
              onTeamModalSubmit();
            }}
          >
            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="team-member-name"
              >
                Name
              </label>
              <JourneyTextInput
                id="team-member-name"
                variant="money"
                hasError={teamModalState.error === teamNameRequiredError}
                value={teamModalState.name}
                onChange={(event) => onTeamModalNameChange(event.target.value)}
              />
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="team-member-role"
              >
                Role
              </label>
              <JourneySelectInput
                id="team-member-role"
                variant="money"
                hasError={
                  teamModalState.error === teamRoleRequiredError ||
                  teamModalState.error === teamRoleNotAvailableError
                }
                value={
                  teamModalState.roleId === ''
                    ? ''
                    : String(teamModalState.roleId)
                }
                onChange={(event) => onTeamModalRoleChange(event.target.value)}
                placeholder="Select role"
                options={teamRoles.map((role) => ({
                  label: role.name,
                  value: String(role.id),
                }))}
              />
            </div>

            <div className="money-form-field">
              <label
                className="money-form-field__label"
                htmlFor="team-member-description"
              >
                Description (optional)
              </label>
              <JourneyTextArea
                id="team-member-description"
                variant="money"
                rows={4}
                value={teamModalState.description}
                onChange={(event) =>
                  onTeamModalDescriptionChange(event.target.value)
                }
              />
            </div>

            <ErrorBanner message={teamModalState.error} />

            <footer className="team-modal__footer">
              <button
                type="button"
                className="money-btn money-btn--secondary money-btn--sm"
                onClick={onCloseTeamModal}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="money-btn money-btn--primary money-btn--sm"
              >
                {teamModalState.mode === 'edit' ? 'Save changes' : 'Add member'}
              </button>
            </footer>
          </form>
        </div>
      </div>
    ) : null}

    <ErrorBanner message={teamError} />

    {!hasApplicationId ? (
      <ErrorBanner
        message={
          'We could not determine your application. Please return to the previous step and try again.'
        }
      />
    ) : null}
  </div>
);
